import { forwardRef } from 'react';
import type { InputHTMLAttributes, CSSProperties } from 'react';

import type { ColorType } from '@lib/types';
import { cn, cva, type VariantProps, setThisClass } from '@lib/utils';

const rangeVariants = cva(
  [
    // Layout
    'block w-full appearance-none bg-transparent outline-hidden',
    // Thumb styles (webkit)
    '[&::-webkit-slider-thumb]:relative [&::-webkit-slider-thumb]:top-[50%]',
    '[&::-webkit-slider-thumb]:h-[var(--thumb-size)] [&::-webkit-slider-thumb]:w-[var(--thumb-size)]',
    '[&::-webkit-slider-thumb]:-translate-y-1/2 [&::-webkit-slider-thumb]:cursor-pointer',
    '[&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:rounded-full',
    '[&::-webkit-slider-thumb]:border-none [&::-webkit-slider-thumb]:bg-current',
    '[&::-webkit-slider-thumb]:transition-all [&::-webkit-slider-thumb]:active:scale-125',
    // Thumb styles (moz)
    '[&::-moz-range-thumb]:relative [&::-moz-range-thumb]:top-[50%]',
    '[&::-moz-range-thumb]:h-[var(--thumb-size)] [&::-moz-range-thumb]:w-[var(--thumb-size)]',
    '[&::-moz-range-thumb]:cursor-pointer [&::-moz-range-thumb]:appearance-none',
    '[&::-moz-range-thumb]:rounded-full [&::-moz-range-thumb]:border-none',
    '[&::-moz-range-thumb]:bg-current [&::-moz-range-thumb]:transition-all',
    '[&::-moz-range-thumb]:active:scale-125',
    // Track styles (webkit)
    '[&::-webkit-slider-runnable-track]:h-[var(--track-h)]',
    '[&::-webkit-slider-runnable-track]:w-full',
    '[&::-webkit-slider-runnable-track]:rounded-full',
    '[&::-webkit-slider-runnable-track]:bg-surface-raised',
    // Track styles (moz)
    '[&::-moz-range-track]:h-[var(--track-h)]',
    '[&::-moz-range-track]:w-full',
    '[&::-moz-range-track]:rounded-full',
    '[&::-moz-range-track]:bg-surface-raised',
    // Disabled state
    'disabled:cursor-not-allowed disabled:opacity-50',
    'disabled:[&::-webkit-slider-thumb]:cursor-not-allowed disabled:[&::-webkit-slider-thumb]:active:scale-100',
    'disabled:[&::-moz-range-thumb]:cursor-not-allowed disabled:[&::-moz-range-thumb]:active:scale-100',
  ],
  {
    variants: {
      color: {
        neutral: 'text-fg-muted',
        primary: 'text-this',
        secondary: 'text-this',
        info: 'text-this',
        success: 'text-this',
        warning: 'text-this',
        error: 'text-this',
      },
      size: {
        xs: '[--thumb-size:0.75rem] [--track-h:0.125rem]',
        sm: '[--thumb-size:0.875rem] [--track-h:0.25rem]',
        md: '[--thumb-size:1.125rem] [--track-h:0.375rem]',
        lg: '[--thumb-size:1.375rem] [--track-h:0.5rem]',
        xl: '[--thumb-size:1.625rem] [--track-h:0.625rem]',
      },
    },
    defaultVariants: {
      color: 'neutral',
      size: 'md',
    },
  }
);

type RangeProps = Omit<InputHTMLAttributes<HTMLInputElement>, 'type' | 'size'> & {
  color?: ColorType;
  thumbSize?: string;
  trackSize?: string;
  style?: CSSProperties & {
    '--thumb-size'?: string;
    '--track-h'?: string;
  };
} & VariantProps<typeof rangeVariants>;

const Range = forwardRef<HTMLInputElement, RangeProps>(
  ({ className, color = 'neutral', size = 'md', thumbSize, trackSize, style, ...rest }, ref) => {
    return (
      <input
        type="range"
        className={cn(
          rangeVariants({ color, size }),
          color !== 'neutral' && setThisClass(color),
          className
        )}
        ref={ref}
        style={{
          ...(thumbSize && { '--thumb-size': thumbSize }),
          ...(trackSize && { '--track-h': trackSize }),
          ...style,
        }}
        {...rest}
      />
    );
  }
);

Range.displayName = 'Range';

export { Range };
export type { RangeProps };
